import classNames from 'classnames';
import React from 'react';

import * as logger from '../../../logger';
import InputGroupContext from '../../../scenes/InputGroupContext';
import ContextualHelpModal from '../../ContextualHelpModal';
import InfoLabel from '../../ContextualHelpModal/InfoLabel';

import { Button } from '../../Button';
import { CheckMark, CheckMarkTypes } from '../../CheckMark';
import { SelectInput } from '../../SelectInput';
import { Option, getOptionsFromArray, isStringArray } from '../../utils';

import styles from '../index.module.css';
import { TextInput } from '../../TextInput';

interface Props {
  onChange: (value: string) => void | Promise<void>;
  value: string;
  options?: Option<string>[] | string[];
  listClassName?: string;
  localizedOptions?: string[];
  helpDialog?: string;
  helpDialogLabel?: string;
  useAlternateEnum?: boolean;
  disabled?: boolean;
  ariaLabelledBy?: string;
  onCustomValueChange?: (value: string) => void | Promise<void>;
  customValue?: string;
}

const LONG_OPTIONS_LIMIT = 15;

const Enum: React.FC<Props> = ({
  options,
  value,
  onChange,
  onCustomValueChange,
  useAlternateEnum,
  localizedOptions,
  listClassName,
  helpDialog,
  helpDialogLabel,
  disabled,
  ariaLabelledBy,
  customValue,
}) => {
  const inputGroup = React.useContext(InputGroupContext);
  const groupId = ariaLabelledBy || inputGroup;

  const [showHelp, setShowHelp] = React.useState(false);
  if (!options?.length) {
    logger.error(new Error('[Enum] options are required with primitive ENUM'));
    return null;
  }

  const enumOptions = isStringArray(options)
    ? getOptionsFromArray(options)
    : options;

  const maybeRenderHelpDialog = () => {
    if (!helpDialog) return null;
    return (
      <>
        <ContextualHelpModal
          headerText="More information"
          helpText={helpDialog}
          isOpen={showHelp}
          onRequestClose={() => setShowHelp(false)}
          onHandleHelpful={async () => setShowHelp(false)}
        />
        <InfoLabel
          onClick={() => setShowHelp(true)}
          label={helpDialogLabel || 'Need help?'}
        />
      </>
    );
  };

  if (useAlternateEnum) {
    return (
      <div
        className={styles.AlternateEnum}
        role="radiogroup"
        aria-labelledby={groupId}
      >
        {maybeRenderHelpDialog()}
        {enumOptions.map((option, i) => (
          <Button
            key={`alternate-enum-${option.key}`}
            className={styles.Button}
            children={
              localizedOptions && localizedOptions[i]
                ? localizedOptions[i]
                : option.display
            }
            onClick={(event: React.FormEvent<HTMLButtonElement>) => {
              event.stopPropagation();
              event.preventDefault();
              onChange(option.value);
            }}
            disabled={disabled}
          />
        ))}
      </div>
    );
  }

  if (options.length > LONG_OPTIONS_LIMIT) {
    return (
      <SelectInput
        value={value}
        onChange={onChange}
        options={enumOptions}
        localizedOptions={localizedOptions}
      />
    );
  }

  return (
    <>
      <div
        className={classNames('question-enum', listClassName)}
        role="radiogroup"
        aria-labelledby={groupId}
      >
        {enumOptions.map((option, i) => (
          <CheckMark
            key={option.key}
            label={localizedOptions ? localizedOptions[i] : option.display}
            value={option.value}
            checked={option.value === value}
            type={CheckMarkTypes.RADIO}
            onClick={() => {
              const updatedValue = option.value === value ? '' : option.value;
              onChange(updatedValue);
            }}
          />
        ))}
        {onCustomValueChange && value === 'OTHER' && (
          <div className={styles.SmallCheckMarkRowMargin}>
            <TextInput
              id="other-raw-input"
              value={customValue}
              className={styles.SmallCheckMarkRowMargin}
              onBlur={() => {
                onCustomValueChange(customValue || '');
              }}
              onChange={(
                event: React.ChangeEvent<
                  HTMLTextAreaElement | HTMLInputElement
                >,
              ) => {
                onCustomValueChange(event.target.value || '');
              }}
            />
          </div>
        )}
      </div>
      {maybeRenderHelpDialog()}
    </>
  );
};

export default Enum;
